import {useAddToCartOffCanvas, useAppDispatch} from "../../redux/stateHooks";
import {useAppContext} from "../useAppContext";
import {useLazyQuery} from "@apollo/client";
import {erpService} from "../../api/erpService";
import {CartItemType} from "../../api/cart";
import {addToCartOffCanvasActions, cartActions, findCartItem} from "../../redux/slices/carts";
import {artworkOffCanvasActions} from "../../redux/slices/artworkOffCanvas";
import {CartServiceResponseType, RecommendationType} from "../../definitions/enums";
import {Nullable, OffCanvasVariableType, PromotionType} from "../../definitions/types";
import {BrazeAddToCartEvent, BrazeItem} from "../../definitions/interfaces";
import {useOffCanvasProductQuery} from "../useOffCanvasProductQuery";
import {defaultFourBeeImageSource} from "../../utils/constants";
import {
    CommercePlatformCartServiceModelsDTOsPublicCartGroupDTO,
    CommercePlatformCartServiceModelsDTOsPublicCartItemDTO
} from "../../api/openApi/cartService";
import {OffCanvasProductModel} from "../../models/domain/offCanvasProductModel";
import {ApolloClients} from "../../api/clients";

const defaultPrice = '0';
const recommendationType = RecommendationType.UsersWhoBoughtAlsoBought;

export const useCartService = () => {
    const dispatch = useAppDispatch();
    const offCanvas = useAddToCartOffCanvas();
    const {currencyCode, catalogId} = useAppContext()
    const {query: getOffCanvasProductQuery} = useOffCanvasProductQuery();
    const [getOffCanvasPromotionQuery] = useLazyQuery(erpService.product.promotion);

    const initiateCart = (request: CartItemType) => {
        dispatch(cartActions.initialize());
        dispatch(cartActions.setRequest(request));
        dispatch(addToCartOffCanvasActions.setShow(true));
        dispatch(artworkOffCanvasActions.setShow(false));
    }

    /**
     * Creates a BrazeAddToCartEvent object
     * @constructor
     * @param brazeItem
     */
    const createBrazeAddToCartEvent = (brazeItem: BrazeItem) => ({
        event: 'brazeAddToCart',
        brazeItem
    } as BrazeAddToCartEvent);

    function publishDataLayerEvent(brazeAddToCartEvent: BrazeAddToCartEvent) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(brazeAddToCartEvent);
    }

    async function processCreateItemResponseAsync(isSuccessfulRequest: boolean, variables: OffCanvasVariableType) {
        const productResponse = await getOffCanvasProductQuery(variables);

        const promotionResponse = await getOffCanvasPromotionQuery({
            context: {
                clientName: ApolloClients.ErpService
            },
            variables: {productId: variables.productId}
        });

        // Break out of the function if the request was not successful or the product response is null
        if (!isSuccessfulRequest || !productResponse) return {
            product: undefined,
            promotion: undefined
        }

        // Deconstruct the product response
        const {sku, title, url, priceModel, media: {images: {dot4b}}} = productResponse;

        // Publish the Braze event to the data layer
        publishDataLayerEvent(createBrazeAddToCartEvent({
            sku,
            title,
            url,
            productId: String(variables.productId),
            quantity: String(variables.quantity ?? productResponse.stockInformation?.minQuantity ?? 1),
            price: priceModel ? priceModel.currentPrice.toFixed(2) : defaultPrice,
            originalPrice: priceModel && priceModel.onSale ? priceModel.unitPrice.toFixed(2) : defaultPrice,
            imageUrl: (dot4b[0]?.url ?? defaultFourBeeImageSource).replace(/^https?:/, '')
        }));

        return {
            product: productResponse,
            promotion: promotionResponse?.data?.promotion
        }
    }

    const dispatchOffCanvasEvents = (product?: OffCanvasProductModel, promotion?: Nullable<PromotionType>) => {
        // Dispatch the product to the store to be used in the off canvas, for the first product id
        if (product) dispatch(addToCartOffCanvasActions.setProduct(product.product));

        // Dispatch the promotion to the store to be used in the off canvas, for the first product id
        if (promotion) dispatch(addToCartOffCanvasActions.setPromotion(promotion));
    };

    return {
        createItem: async (request: CartItemType) => {
            initiateCart(request);
            const action = await dispatch(cartActions.createItemAsync({currencyCode, catalogId, item: request}));
            const cartItem = findCartItem(action.payload?.response, request);

            // The request is successful if the cart item is returned in the response
            const isSuccessfulRequest = !!(action.payload?.errorType === CartServiceResponseType.Success && cartItem);

            // If the request is successful, we can use the cart item to get the product, otherwise we use the request
            const variables: OffCanvasVariableType = isSuccessfulRequest ? {
                ...cartItem,
                recommendationType,
                currencyCode,
                productId: cartItem.group!.productId!,
                quantity: cartItem.quantity
            } : {
                ...request,
                recommendationType,
                currencyCode
            };

            // Execute the query for the product and promotion data
            const {product, promotion} = await processCreateItemResponseAsync(isSuccessfulRequest, variables);

            // Dispatch the product and promotion to the store to be used in the off canvas
            dispatchOffCanvasEvents(product, promotion);

            dispatch(addToCartOffCanvasActions.setOffCanvasPending(false));
        },

        createItems: async (productIds: number[]) => {
            // If there are no product ids, we don't need to make a request
            if (productIds.length === 0) return;

            // Map the request for the first product id
            initiateCart({
                productId: productIds[0],
                quantity: null,
                artworkId: null,
                artworkName: null,
                artworkNotes: null,
                width: null,
                height: null
            });

            // Map the requests for the product ids
            const items = productIds.map(productId => ({
                productId,
                quantity: null,
                artworkId: null,
                artworkName: null,
                artworkNotes: null,
                width: null,
                height: null
            }));

            // Make the request
            const action = await dispatch(cartActions.createItemsAsync({
                currencyCode,
                catalogId,
                items
            }));

            for (let i = 0; i < productIds.length; i++) {
                const productId = productIds[i];

                const cartGroup =
                    action.payload?.response?.cart.groups?.filter((cartGroup): cartGroup is CommercePlatformCartServiceModelsDTOsPublicCartGroupDTO => cartGroup !== null)
                        .filter(cartGroup => cartGroup.productId == productId)[0];

                if (!cartGroup) continue;

                const cartItem = cartGroup.items?.filter((cartItem): cartItem is CommercePlatformCartServiceModelsDTOsPublicCartItemDTO => cartItem !== null)
                    .filter(cartItem => cartItem.artworkId == null)
                    .filter(cartItem => cartItem.width == null)
                    .filter(cartItem => cartItem.height == null)[0];

                if (!cartItem) continue;

                // The request is successful if the cart item is returned in the response
                const isSuccessfulRequest = !!(action.payload?.errorType === CartServiceResponseType.Success && cartGroup);

                // If the request is successful, we can use the cart item to get the product,
                // otherwise we use the request
                const variables: OffCanvasVariableType = isSuccessfulRequest ? {
                    ...cartGroup,
                    recommendationType,
                    currencyCode,
                    productId: cartGroup.productId!,
                    quantity: cartItem.quantity
                } : {
                    productId,
                    recommendationType,
                    currencyCode
                };

                // Execute the query for the product and promotion data
                const {product, promotion} = await processCreateItemResponseAsync(isSuccessfulRequest, variables);

                // Only dispatch the product and promotion for the first product id
                if (i > 0) continue;

                dispatchOffCanvasEvents(product, promotion);
            }

            dispatch(addToCartOffCanvasActions.setOffCanvasPending(false));
        },

        createItemResources: async (request: CartItemType) => {
            initiateCart(request);
            dispatch(cartActions.createItemResources(request));
            const variables = {
                productId: request.productId,
                recommendationType: RecommendationType.UsersWhoBoughtAlsoBought,
                currencyCode: currencyCode,
                quantity: request.quantity ?? 0,
                width: request.width,
                height: request.height
            }
            const productResponse = await getOffCanvasProductQuery(variables);
            const promotionResponse = await getOffCanvasPromotionQuery({variables});
            if (productResponse) dispatch(addToCartOffCanvasActions.setProduct(productResponse.product));
            if (promotionResponse?.data?.promotion) dispatch(addToCartOffCanvasActions.setPromotion(promotionResponse.data.promotion));
            dispatch(addToCartOffCanvasActions.setOffCanvasPending(false));
        },

        setShow: (show: boolean) => {
            return dispatch(addToCartOffCanvasActions.setShow(show));
        },

        offCanvas: offCanvas,
    }
}